import {
  ClockCircleOutlined,
  DeleteOutlined,
  FolderOpenOutlined,
  PictureOutlined,
  StarOutlined,
  UnorderedListOutlined
} from "@ant-design/icons"
import { Flex, Tooltip, Typography } from "antd"
import React from "react"
import { useTranslation } from "react-i18next"

import { type FilesNav } from "../types/files-nav.types.ts"

const { Text } = Typography

interface NavItem {
  key: FilesNav
  icon: React.ReactNode
  labelKey: string
  disabled?: boolean
}

const NAV_ITEMS: NavItem[] = [
  {
    key: "all",
    icon: <UnorderedListOutlined />,
    labelKey: "files.sidebar.all"
  },
  {
    key: "recent",
    icon: <ClockCircleOutlined />,
    labelKey: "files.sidebar.recent"
  },
  {
    key: "folders",
    icon: <FolderOpenOutlined />,
    labelKey: "files.sidebar.folders"
  },
  {
    key: "albums",
    icon: <PictureOutlined />,
    labelKey: "files.sidebar.albums"
  },
  {
    key: "favorites",
    icon: <StarOutlined />,
    labelKey: "files.sidebar.favorites"
  },
  {
    key: "trash",
    icon: <DeleteOutlined />,
    labelKey: "files.sidebar.trash"
  }
]

interface FilesSidebarProps {
  active: FilesNav
  onChange: (nav: FilesNav) => void
}

export function FilesSidebar({ active, onChange }: FilesSidebarProps) {
  const { t } = useTranslation()

  return (
    <Flex
      vertical
      gap={2}
      className="w-[180px] shrink-0 pr-3 border-r border-border"
    >
      <Text
        type="secondary"
        className="text-xs uppercase tracking-wide px-2 mb-1"
      >
        {t("files.sidebar.title")}
      </Text>
      {NAV_ITEMS.map((item) => {
        const button = (
          <button
            key={item.key}
            type="button"
            disabled={item.disabled}
            tabIndex={item.disabled ? -1 : 0}
            aria-disabled={item.disabled}
            aria-pressed={active === item.key}
            onClick={() => !item.disabled && onChange(item.key)}
            className={`flex items-center gap-2 rounded-md px-2 py-1.5 text-sm text-left transition-colors ${
              item.disabled
                ? "cursor-not-allowed"
                : active === item.key
                  ? "bg-primary/10 text-primary font-medium"
                  : "hover:bg-muted"
            }`}
          >
            <Text
              disabled={item.disabled}
              className={active === item.key ? "!text-primary" : undefined}
            >
              <span className="mr-2">{item.icon}</span>
              {t(item.labelKey)}
            </Text>
          </button>
        )

        return item.disabled ? (
          <Tooltip
            key={item.key}
            title={t("files.sidebar.comingSoon")}
            placement="right"
          >
            {button}
          </Tooltip>
        ) : (
          button
        )
      })}
    </Flex>
  )
}
